import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE } from 'api/utilityActionTypes';

import { hasRolePartner } from 'common/utility/userRoles';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';

const getFormFields = ({
  onAutocompleteAddress,
  addressMatches,
  setAddressMatches,
  setAddressNotFound,
  shouldRenderSuggestions,
  setShouldRenderSuggestions,
  isTenantScreeningPartner,
  formValues = {},
}) => {
  const showHasUnitNumber =
    isTenantScreeningPartner &&
    formValues.address &&
    formValues.addressInput.length &&
    Object.keys(formValues.address).length &&
    !!formValues.address?.place_id;
  return [
    {
      id: 'addressInput',
      title: 'Address',
      type: 'autocomplete',
      properties: {
        placeholder: 'type address here...',
        choices: (addressMatches || [])
          .filter(
            ({ id, display }) =>
              formValues.address?.id !== id || formValues.addressInput !== display,
          )
          .map(({ display }) => display),
        onSuggestionSelected: ({ newValue, formikBag }) => {
          const addressMatch = (addressMatches || []).find(({ display }) => newValue === display);
          formikBag.setFieldValue('address', addressMatch);
          formikBag.setFieldValue('addressInput', newValue);
          setAddressMatches([]);
          setAddressNotFound(false);
          setShouldRenderSuggestions(false);
        },
        shouldRenderSuggestions: () => shouldRenderSuggestions,
        filterFunc: () => {
          return true;
        },
        autoFocus: true,
        alwaysRenderSuggestions: !formValues.address,
        onChange: onAutocompleteAddress,
      },
    },
    showFieldIf(showHasUnitNumber, {
      id: 'hasUnitNumber',
      title: 'Do you have a unit number?',
      type: 'yes_no',
      properties: {
        labels: {
          yes: 'Yes',
          no: 'No unit',
        },
        width: '100%',
        gridGap: '8px',
        autoFitWidth: '100%',
      },
    }),
    showFieldIf(formValues.hasUnitNumber, {
      id: 'unitNumber',
      title: 'Unit Number',
      type: 'short_text',
      properties: {
        placeholder: 'Write unit number ...',
      },
    }),
    {
      id: 'address',
      type: 'hidden',
    },
  ];
};

const SrUtilityAddressSearchForm = ({
  onSubmit,
  onAddressNotFound,
  dispatch,
  utilityUser,
  utilityPartnerId,
  isTenantScreeningPartner,
}) => {
  const [addressMatches, setAddressMatches] = useState([]);
  const [addressNotFound, setAddressNotFound] = useState(false);
  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(false);
  const lastAutocompleteSearchRef = useRef(null);

  const isPartner = hasRolePartner(utilityUser.roles);

  const onAutocompleteAddress = ({ newValue }) => {
    lastAutocompleteSearchRef.current = newValue;
    setShouldRenderSuggestions(true);
    if (!newValue) {
      setAddressMatches([]);
    } else {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE,
        queryId: newValue,
        payload: {
          input: newValue,
          utilityPartnerId,
          withRequiredTypes: true,
          includeGoogleMapsSuggestions: !!isTenantScreeningPartner,
        },
      }).then((addresses) => {
        if (lastAutocompleteSearchRef.current === newValue) {
          setAddressMatches(addresses);
        }
      });
    }
  };

  const _handleSubmit = ({ addressInput, address, unitNumber }) => {
    if (addressInput && !address) {
      setAddressNotFound(true);
      return onAddressNotFound({ addressInput });
    }
    onSubmit({ address, addressInput, unitNumber });
  };

  const formFields = getFormFields({
    addressMatches,
    onAutocompleteAddress,
    shouldRenderSuggestions,
    setShouldRenderSuggestions,
    setAddressMatches,
    setAddressNotFound,
    isTenantScreeningPartner,
  });

  return (
    <Formik
      initialValues={createInitialValues({ formFields })}
      enableReinitialize
      validate={createFormValidationFn(formFields)}
      onSubmit={_handleSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          addressMatches,
          onAutocompleteAddress,
          setAddressMatches,
          setAddressNotFound,
          shouldRenderSuggestions,
          setShouldRenderSuggestions,
          formValues: formikBag.values,
          isTenantScreeningPartner,
          setFieldValue: formikBag.setFieldValue,
        });

        const shouldShowContinueButton = !!formikBag?.values?.addressInput;

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            <SrBox padding="28px 0" display={shouldShowContinueButton ? 'block' : 'none'}>
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                disabled={!formikBag.values.addressInput || isPartner}
                heightType={SR_BUTTON_HEIGHT_MEDIUM}
                hasBoxShadow
                type="submit"
              >
                See Utilities
              </SrButton>
              {formikBag.values.addressInput && isPartner && (
                <SrBox marginTop="12px" borderRadius="3px">
                  <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                    Partners can't continue to the activation flow.
                  </SrErrorMessage>
                </SrBox>
              )}
              {addressNotFound && (
                <SrBox marginTop="12px" borderRadius="3px">
                  <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                    No address was found.
                    <br /> If you think this was a mistake, please email us at
                    reminders@utility-setup.com
                  </SrErrorMessage>
                </SrBox>
              )}
            </SrBox>
          </Form>
        );
      }}
    />
  );
};

SrUtilityAddressSearchForm.propTypes = {
  onAddressNotFound: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  utilityPartnerId: PropTypes.number,
  utilityUser: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  isTenantScreeningPartner: PropTypes.bool,
};

SrUtilityAddressSearchForm.displayName = 'SrUtilityAddressSearchForm';

export default SrUtilityAddressSearchForm;
